import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

import { Label } from '../../components/ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Plus, Edit2, Trash2, Search, FileText, RefreshCw, Download, Upload, CheckSquare, Calculator, Eye, Copy, Archive, BarChart3, Layers, Building, Target, Tag } from 'lucide-react';

import BackButton from '../../components/BackButton';
import BulkLinkDispositifsModal from '../../components/BulkLinkDispositifsModal';
import BulkActionsMenu from '../../components/BulkActionsMenu';
import { ACTIVITY_STATUS, STATUS_BADGE_COLORS } from './nomenclatureConstants';
import { useNomenclature } from './useNomenclature';
import NomenclatureModals from './NomenclatureModals';
import { AssignDispositifsDialog, AssignEstimateursDialog } from './AssignDialogs';

function getStatusBadge(status) {
  const cfg = ACTIVITY_STATUS.find((s) => s.value === status) || ACTIVITY_STATUS[0];
  const Icon = cfg.icon;
  const cls = STATUS_BADGE_COLORS[cfg.color] || STATUS_BADGE_COLORS.gray;
  return (<Badge className={cls}><Icon className="h-3 w-3 mr-1" />{cfg.label}</Badge>);
}

export default function NomenclatureManagementPro() {
  const ctx = useNomenclature();
  const { collections, selectedCollection, setSelectedCollection, nomenclatures: nomenclatures, categories, dispositifs, estimateurs, loading, searchTerm, setSearchTerm, selectedCategory, setSelectedCategory, selectedStatus, setSelectedStatus, selectedDispositif, setSelectedDispositif, showArchived, setShowArchived, selectedIds, setSelectedIds, selectionMode, setSelectionMode, setCreateDialogOpen, setViewDialogOpen, setImportDialogOpen, setExportDialogOpen, assignDispositifDialogOpen, setAssignDispositifDialogOpen, bulkLinkOpen, setBulkLinkOpen, setBulkLinkInitialTab, assignEstimateurDialogOpen, setAssignEstimateurDialogOpen, setStatsDialogOpen, setCurrentItem, stats, filteredNomenclatures, fetchNomenclatures, handleDelete, handleDeleteAll, handleArchive, handleArchiveAll, handleDuplicate, handleAssignDispositifs, handleAssignEstimateurs, resetForm, openEditDialog, toggleSelection, selectAll } = ctx;

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 min-h-screen">
      
      {/* Bouton Retour */}
      <BackButton label="Retour" />

{/* Header Professionnel */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Layers className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Gestion des Nomenclatures</h1>
              <p className="text-white/80 text-sm">Gérez les activités, catégories, codes et définitions</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Statistiques rapides */}
            <div className="flex gap-2">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-xs text-white/70">Total</div>
              </div>
              <div className="bg-green-500/30 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.active}</div>
                <div className="text-xs text-white/70">Actifs</div>
              </div>
              <div className="bg-amber-500/30 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.pending}</div>
                <div className="text-xs text-white/70">En attente</div>
              </div>
            </div>
            
            {/* Actions principales */}
            <Button onClick={() => setStatsDialogOpen(true)} variant="ghost" className="text-white hover:bg-white/20">
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button onClick={fetchNomenclatures} variant="ghost" className="text-white hover:bg-white/20">
              <RefreshCw className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Barre d'outils */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Sélection de collection */}
            <div className="flex items-center gap-3">
              <Select 
                value={selectedCollection?.id || ''} 
                onValueChange={(val) => {
                  const col = collections.find(c => c.id === val);
                  setSelectedCollection(col);
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Sélectionner collection" />
                </SelectTrigger>
                <SelectContent>
                  {collections.map(col => (
                    <SelectItem key={col.id} value={col.id}>
                      {col.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-[250px]"
                />
              </div>
            </div>
            
            {/* Filtres */}
            <div className="flex items-center gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  {ACTIVITY_STATUS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedDispositif} onValueChange={setSelectedDispositif}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Dispositif" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous dispositifs</SelectItem>
                  {dispositifs.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nom || d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2 ml-2">
                <Switch checked={showArchived} onCheckedChange={setShowArchived} />
                <Label className="text-sm text-slate-600">Voir archivés</Label>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button 
                variant={selectionMode ? "default" : "outline"} 
                size="sm"
                onClick={() => {
                  setSelectionMode(!selectionMode);
                  if (selectionMode) setSelectedIds(new Set());
                }}
              >
                <CheckSquare className="h-4 w-4 mr-1" />
                {selectionMode ? `${selectedIds.size} sélectionné(s)` : 'Sélection'}
              </Button>
              
              {selectionMode && selectedIds.size > 0 && (
                <BulkActionsMenu
                  selectedNomenclatures={filteredNomenclatures.filter((n) => selectedIds.has(n.id))}
                  collections={collections}
                  onApplied={() => {
                    fetchNomenclatures();
                    setSelectedIds(new Set());
                  }}
                  onOpenBulkLink={(tab) => {
                    setBulkLinkInitialTab(tab);
                    setBulkLinkOpen(true);
                  }}
                />
              )}
              
              {selectionMode && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleArchiveAll}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    <Archive className="h-4 w-4 mr-1" />
                    Tout archiver ({filteredNomenclatures.length})
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDeleteAll}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Tout supprimer ({filteredNomenclatures.length})
                  </Button>
                </>
              )}
              
              <div className="border-l pl-2 ml-2 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-1" />
                  Importer
                </Button>
                <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)}>
                  <Download className="h-4 w-4 mr-1" />
                  Exporter
                </Button>
                <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="h-4 w-4 mr-1" />
                  Nouvelle activité
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des nomenclatures */}
      <div className="grid gap-4">
        {loading ? (
          <Card className="p-12 text-center">
            <RefreshCw className="h-8 w-8 mx-auto animate-spin text-indigo-600 mb-4" />
            <p className="text-slate-600">Chargement des nomenclatures...</p>
          </Card>
        ) : filteredNomenclatures.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucune nomenclature trouvée</h3>
            <p className="text-slate-500 mb-4">Modifiez vos filtres ou créez une nouvelle nomenclature</p>
            <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Créer une nomenclature
            </Button>
          </Card>
        ) : (
          <div className="grid gap-3">
            {/* En-tête de sélection */}
            {selectionMode && (
              <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <Checkbox 
                  checked={selectedIds.size === filteredNomenclatures.length}
                  onCheckedChange={selectAll}
                />
                <span className="text-sm text-indigo-700 font-medium">
                  Tout sélectionner ({filteredNomenclatures.length} éléments)
                </span>
              </div>
            )}
            
            {filteredNomenclatures.map((item) => (
              <Card 
                key={item.id} 
                className={`border-0 shadow-md hover:shadow-lg transition-all duration-200 ${
                  selectedIds.has(item.id) ? 'ring-2 ring-indigo-500 bg-indigo-50/50' : ''
                } ${item.status === 'archived' || item.is_archived ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Checkbox de sélection */}
                    {selectionMode && (
                      <Checkbox 
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={() => toggleSelection(item.id)}
                        className="mt-1"
                      />
                    )}
                    
                    {/* Contenu principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        {item.code && (
                          <Badge variant="outline" className="font-mono bg-slate-700 text-white border-slate-800 font-semibold">
                            {item.code}
                          </Badge>
                        )}
                        <h3 className="font-semibold text-slate-800 truncate">{item.name}</h3>
                        {getStatusBadge(item.status || (item.is_active ? 'active' : 'inactive'))}
                        
                        {item.applicable_devenir_membre && (
                          <Badge className="bg-blue-700 text-white font-semibold">
                            <Target className="h-3 w-3 mr-1" />
                            Devenir Membre
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          {item.category}
                        </span>
                        {(item.dispositifs?.length || 0) > 0 && (
                          <span className="flex items-center gap-1">
                            <Building className="h-3 w-3" />
                            {item.dispositifs.length} dispositif(s)
                          </span>
                        )}
                        {(item.estimateurs?.length || 0) > 0 && (
                          <span className="flex items-center gap-1">
                            <Calculator className="h-3 w-3" />
                            {item.estimateurs.length} estimateur(s)
                          </span>
                        )}
                      </div>
                      
                      {item.definition && (
                        <p className="text-sm text-slate-600 line-clamp-2">{item.definition}</p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setCurrentItem(item); setViewDialogOpen(true); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(item)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleArchive(item.id)}>
                        <Archive className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <NomenclatureModals ctx={ctx} />
      {/* Dialog Assignation Dispositifs en masse */}
      <AssignDispositifsDialog 
        open={assignDispositifDialogOpen}
        onOpenChange={setAssignDispositifDialogOpen}
        dispositifs={dispositifs}
        selectedCount={selectedIds.size}
        onAssign={(dispositifIds) => handleAssignDispositifs(Array.from(selectedIds), dispositifIds)}
      />

      {/* Dialog Assignation Estimateurs en masse */}
      <AssignEstimateursDialog 
        open={assignEstimateurDialogOpen}
        onOpenChange={setAssignEstimateurDialogOpen}
        estimateurs={estimateurs}
        selectedCount={selectedIds.size}
        onAssign={(estimateurIds) => handleAssignEstimateurs(Array.from(selectedIds), estimateurIds)}
      />
      {/* V36.19 — Lier en masse (Dispositifs + Estimateurs + modes add/replace/remove) */}
      <BulkLinkDispositifsModal
        open={bulkLinkOpen}
        onOpenChange={setBulkLinkOpen}
        selectedNomenclatures={filteredNomenclatures.filter((n) => selectedIds.has(n.id))}
        dispositifs={dispositifs}
        estimateurs={estimateurs}
        onApplied={() => {
          fetchNomenclatures();
          setSelectedIds(new Set());
        }}
      />
    </div>
  );
}
